/*!
 * Copyright © 2005-2025 Hyland Software, Inc. and its affiliates. All rights reserved.
 *
 * Alfresco Example Content Application
 *
 * This file is part of the Alfresco Example Content Application.
 * If the software was purchased under a paid Alfresco license, the terms of
 * the paid license agreement will prevail. Otherwise, the software is
 * provided under the following open source license terms:
 *
 * The Alfresco Example Content Application is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Lesser General Public License as published by
 * the Free Software Foundation, either version 3 of the License, or
 * (at your option) any later version.
 *
 * The Alfresco Example Content Application is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
 * GNU Lesser General Public License for more details.
 *
 * You should have received a copy of the GNU Lesser General Public License
 * from Hyland Software. If not, see <http://www.gnu.org/licenses/>.
 */

import { EnvironmentProviders, inject, NgModule, Provider, provideAppInitializer } from '@angular/core';
import { provideExtensionConfig, provideExtensions } from '@alfresco/adf-extensions';
import { provideFolderRulesExtension } from '@alfresco/aca-content/folder-rules';
import { provideAosExtension } from '@alfresco/aca-content/ms-office';
import { DEV_MODE_TOKEN, PACKAGE_JSON, provideAboutExtension } from '@alfresco/aca-content/about';
import { environment } from '../environments/environment';
import packageJson from 'package.json';
import { ThemeService } from './components/theme/theme.service';
import { PreviewRedirectService } from './components/preview/preview-redirect.service';
import { SubmenuToggleService } from './components/theme/submenu-toggle.service';
import { ThemeToggleComponent } from './components/theme/theme-toggle.component';
import { ThemeToggleRowComponent } from './components/theme/theme-toggle-row.component';
import { SidebarLogoutComponent } from './components/theme/sidebar-logout.component';
import { SidebarVersionComponent } from './components/version/sidebar-version.component';
import { StartWorkflowActionComponent } from './components/workflow/start-workflow-action.component';
import { PermissionsTabComponent } from './components/info-drawer/permissions-tab.component';
import { SetExpiryActionComponent } from './components/expiry/set-expiry-action.component';
import { ExpiryStatusColumnComponent } from './components/expiry/expiry-status-column.component';
import { ThaiDateColumnComponent } from './components/documents/thai-date-column.component';
import { DmsFieldColumnComponent } from './components/documents/dms-field-column.component';
import { ShellHeaderComponent } from './components/shell-header/shell-header.component';
import { StorageUsageComponent } from './components/storage/storage-usage.component';
import { WorkflowSettingsService } from './components/workflow/workflow-settings.service';
import { MenuPermissionsService } from './components/menu-permissions/menu-permissions.service';

/**
 * Filled by the app initializer below. Rule evaluators run whenever the
 * extension service evaluates — outside an injection context — so they cannot
 * `inject()`; the initializer (which does run in one) hands the service over.
 * Until then the flag reads as enabled, the deployment the app shipped with.
 */
let workflowSettings: WorkflowSettingsService | null = null;
let menuPermissions: MenuPermissionsService | null = null;

export function provideApplicationExtensions(): (Provider | EnvironmentProviders)[] {
  return [
    ...provideAboutExtension(),
    ...provideAosExtension(),
    ...provideFolderRulesExtension(),
    { provide: PACKAGE_JSON, useValue: packageJson },
    { provide: DEV_MODE_TOKEN, useValue: !environment.production },

    // Custom modernization: dashboard route + Light/Dark theme toggle
    provideExtensionConfig(['app.custom.extensions.json']),
    provideExtensions({
      components: {
        'app.layout.header': ShellHeaderComponent,
        'app.theme-toggle': ThemeToggleComponent,
        'app.sidebar.theme-toggle-row': ThemeToggleRowComponent,
        'app.sidebar.logout': SidebarLogoutComponent,
        'app.sidebar.storage': StorageUsageComponent,
        'app.sidebar.version': SidebarVersionComponent,
        'app.workflow.start-action': StartWorkflowActionComponent,
        'app.components.tabs.permissions': PermissionsTabComponent,
        'app.expiry.set-action': SetExpiryActionComponent,
        'app.columns.expiry-status': ExpiryStatusColumnComponent,
        'app.columns.thai-date': ThaiDateColumnComponent,
        'app.columns.dms-field': DmsFieldColumnComponent
      },
      evaluators: {
        // Used by the sidenav to show the Workflow Builder to admins only.
        'app.isAdmin': (context: any): boolean => !!context?.profile?.isAdmin,
        // The administrator's workflow switch — when off, "งานของฉัน" and
        // "การอนุมัติ" leave the navigation and the start-workflow actions hide.
        'app.workflowEnabled': (): boolean => workflowSettings?.enabled() ?? true,
        // One evaluator per controlled menu — `app.menu.upload`, `app.menu.all`, …
        // Denied by default: once a policy exists, a menu shows only to the
        // people and groups a matching policy names. Until the service is
        // handed over the answer is "shown", the deployment the app shipped with.
        ...Object.fromEntries(
          MenuPermissionsService.MENU_ITEMS.map((menu) => [`app.menu.${menu.key}`, () => menuPermissions?.isMenuAllowed(menu.id) ?? true])
        )
      }
    }),
    // Apply the saved Light/Dark choice on every page (including login)
    provideAppInitializer(() => inject(ThemeService).init()),
    // Render opened files in an in-layout document view instead of the overlay viewer
    provideAppInitializer(() => inject(PreviewRedirectService).init()),
    // Collapse sidenav sub-menus by default; expand on click / active route
    provideAppInitializer(() => inject(SubmenuToggleService).init()),
    // Hand the workflow flag to the app.workflowEnabled rule evaluator
    provideAppInitializer(() => {
      workflowSettings = inject(WorkflowSettingsService);
      menuPermissions = inject(MenuPermissionsService);
    })
  ];
}

/* @deprecated use `provideApplicationExtensions()` provider api instead */
@NgModule({
  providers: [...provideApplicationExtensions()]
})
export class AppExtensionsModule {}
